import { useState } from "react";

import { useChartsContext } from "contexts/ChartContext";
import {
  DEFAULT_WIDTH,
  HIGHLIGHTED_WIDTH
} from "entities/charts/eva/translators/constants";

import { useChartFeatureHandlers } from "hooks";
import { useChartEntities } from "hooks/charts";

export const useLineSizeInteraction = () => {
  const { id } = useChartsContext();
  const { handleNumericChange } = useChartFeatureHandlers();
  const { lineSize, highlightedLineSize } = useChartEntities(id);

  // Calculate the default offset between highlighted and normal line size
  const DEFAULT_OFFSET = HIGHLIGHTED_WIDTH - DEFAULT_WIDTH;

  // Track if highlighted line size has been manually changed by user
  const [highlightedLineSizeManuallyChanged, setHighlightedLineSizeManuallyChanged] =
    useState(false);

  const handleLineSizeChange = (val: number | null) => {
    if (val === null) {
      return;
    }

    // Update line size
    handleNumericChange("lineSize")(val);

    // If highlighted line size hasn't been manually changed, update it by maintaining the offset
    if (!highlightedLineSizeManuallyChanged) {
      const newHighlightedLineSize = val + DEFAULT_OFFSET;
      handleNumericChange("highlightedLineSize")(newHighlightedLineSize);
    }

    // If line size is reset to default and highlighted line size is also at its expected value, reset the manual flag
    if (val === DEFAULT_WIDTH) {
      const expectedHighlightedSize = val + DEFAULT_OFFSET; // 2 + 4 = 6
      const currentHighlightedSize =
        highlightedLineSize?.properties?.value ?? HIGHLIGHTED_WIDTH;
      if (currentHighlightedSize === expectedHighlightedSize) {
        setHighlightedLineSizeManuallyChanged(false);
      }
    }
  };

  const handleHighlightedLineSizeChange = (val: number | null) => {
    if (val === null) {
      return;
    }
    setHighlightedLineSizeManuallyChanged(true);
    handleNumericChange("highlightedLineSize")(val);
  };

  const handleHighlightedLineSizeReset = () => {
    const currentLineSize = lineSize?.properties?.value ?? DEFAULT_WIDTH;

    // Always reset to current line size + default offset
    const resetValue = currentLineSize + DEFAULT_OFFSET;
    handleNumericChange("highlightedLineSize")(resetValue);

    setHighlightedLineSizeManuallyChanged(false);
  };

  // Calculate what the highlighted line size should be (for reset button display)
  const getExpectedHighlightedLineSize = () => {
    const currentLineSize = lineSize?.properties?.value ?? DEFAULT_WIDTH;
    // Always expect highlighted line size to be line size + default offset
    return currentLineSize + DEFAULT_OFFSET;
  };

  // Check if reset button should be shown for highlighted line size
  const shouldShowHighlightedLineSizeReset = (currentValue: number) => {
    const expectedValue = getExpectedHighlightedLineSize();
    return currentValue !== expectedValue;
  };

  return {
    handleLineSizeChange,
    handleHighlightedLineSizeChange,
    handleHighlightedLineSizeReset,
    shouldShowHighlightedLineSizeReset
  };
};
